import { useCallback, useState } from "react";
import { Input } from "@repo/ui";
import { Eye, EyeOff } from "lucide-react";

export interface SecureInputProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  onEnter?: () => void;
  placeholder?: string;
  maxLength?: number;
  disabled?: boolean;
  autoFocus?: boolean;
  className?: string;
  "data-testid"?: string;
}

/**
 * A password-style input that never exposes the real value in the DOM.
 *
 * Renders bullet characters (●) instead of the actual text, and captures
 * keystrokes / pastes into state directly. Because the DOM value is always
 * bullets, browsers (Chrome, Safari, etc.) won't trigger their
 * "Save password?" prompt.
 */
export function SecureInput({
  id,
  value,
  onChange,
  onEnter,
  placeholder = "--------",
  maxLength = 16,
  disabled = false,
  autoFocus = false,
  className,
  "data-testid": dataTestId,
}: SecureInputProps) {
  const [visible, setVisible] = useState(false);

  const handleEnterOnly = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" && onEnter) {
        e.preventDefault();
        onEnter();
      }
    },
    [onEnter]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        if (onEnter) {
          e.preventDefault();
          onEnter();
        }
        return;
      }

      if (e.key === "Backspace") {
        e.preventDefault();
        const start = e.currentTarget.selectionStart ?? 0;
        const end = e.currentTarget.selectionEnd ?? 0;
        if (start !== end) {
          onChange(value.slice(0, start) + value.slice(end));
        } else if (start > 0) {
          onChange(value.slice(0, start - 1) + value.slice(start));
        }
        return;
      }

      if (e.key === "Delete") {
        e.preventDefault();
        const start = e.currentTarget.selectionStart ?? 0;
        const end = e.currentTarget.selectionEnd ?? 0;
        if (start !== end) {
          onChange(value.slice(0, start) + value.slice(end));
        } else if (start < value.length) {
          onChange(value.slice(0, start) + value.slice(start + 1));
        }
        return;
      }
    },
    [value, onChange, onEnter]
  );

  const handleBeforeInput = useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      const inputEvent = e.nativeEvent as InputEvent;
      const data = inputEvent.data;

      if (!data) {
        if (inputEvent.inputType?.startsWith("delete")) {
          e.preventDefault();
          const start = e.currentTarget.selectionStart ?? 0;
          const end = e.currentTarget.selectionEnd ?? 0;
          if (start !== end) {
            onChange(value.slice(0, start) + value.slice(end));
          } else if (
            inputEvent.inputType === "deleteContentBackward" &&
            start > 0
          ) {
            onChange(value.slice(0, start - 1) + value.slice(start));
          } else if (
            inputEvent.inputType === "deleteContentForward" &&
            start < value.length
          ) {
            onChange(value.slice(0, start) + value.slice(start + 1));
          }
        }
        return;
      }

      e.preventDefault();
      const start = e.currentTarget.selectionStart ?? 0;
      const end = e.currentTarget.selectionEnd ?? 0;
      const next = value.slice(0, start) + data + value.slice(end);
      onChange(next.slice(0, maxLength));
    },
    [value, onChange, maxLength]
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      const pasted = e.clipboardData.getData("text");
      const start = e.currentTarget.selectionStart ?? 0;
      const end = e.currentTarget.selectionEnd ?? 0;
      const next =
        start !== end
          ? value.slice(0, start) + pasted + value.slice(end)
          : value.slice(0, start) + pasted + value.slice(start);
      onChange(next.slice(0, maxLength));
    },
    [value, onChange, maxLength]
  );

  return (
    <div className="relative">
      <Input
        id={id}
        type="text"
        placeholder={placeholder}
        value={visible ? value : "\u25CF".repeat(value.length)}
        onKeyDown={visible ? handleEnterOnly : handleKeyDown}
        onBeforeInput={visible ? undefined : handleBeforeInput}
        onPaste={visible ? undefined : handlePaste}
        onChange={
          visible
            ? (e) => onChange(e.target.value.slice(0, maxLength))
            : () => {}
        }
        maxLength={maxLength}
        disabled={disabled}
        autoComplete="off"
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        readOnly={false}
        autoFocus={autoFocus}
        className={`pr-10 ${className || ""}`}
        data-testid={dataTestId}
      />
      {value.length > 0 && (
        <button
          type="button"
          tabIndex={-1}
          aria-label={visible ? "Hide password" : "Show password"}
          onClick={() => setVisible((v) => !v)}
          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
        >
          {visible ? (
            <EyeOff className="h-4 w-4" />
          ) : (
            <Eye className="h-4 w-4" />
          )}
        </button>
      )}
    </div>
  );
}
